import type { WidgetTemplate, WidgetStyleConfig } from '@/types/ui/widget-styles';

/**
 * Default widget style configuration (used by Classic template)
 */
export const DEFAULT_WIDGET_CONFIG: WidgetStyleConfig = {
  templateId: 'classic',
  button: {
    shape: 'circle',
    size: 'medium',
    showOnlineIndicator: true,
    showLabel: false,
  },
  popup: {
    size: 'medium',
    borderRadius: 12,
    animation: 'scale',
    shadowIntensity: 'medium',
  },
  theme: {
    style: 'light',
    glassEffect: false,
    accentOnHeader: true,
  },
};

/**
 * Widget template definitions
 * Organized by category: Core, Button Variants, Themes
 */
export const WIDGET_TEMPLATES: WidgetTemplate[] = [
  // ===== CORE STYLES =====
  {
    id: 'magic-v2',
    name: 'Magic',
    description: 'New: soft aura, sparkle, full chat experience inside the popup',
    category: 'core',
    /* Magic loads a different runtime script (/widget-v2.js with HelpGenieV2.init)
       so the config below mainly drives the picker preview + opt-in marketing copy.
       The actual look + feel is owned by widget-v2.js, not these tokens. */
    config: {
      templateId: 'magic-v2',
      button: {
        shape: 'circle',
        size: 'medium',
        showOnlineIndicator: false,
        showLabel: false,
      },
      popup: {
        size: 'large',
        borderRadius: 22,
        animation: 'scale',
        shadowIntensity: 'heavy',
      },
      theme: {
        style: 'light',
        glassEffect: false,
        accentOnHeader: true,
      },
    },
  },
  {
    id: 'classic',
    name: 'Classic',
    description: 'Familiar floating button with popup window',
    category: 'core',
    recommended: true,
    config: DEFAULT_WIDGET_CONFIG,
  },
  {
    id: 'compact',
    name: 'Compact',
    description: 'Smaller footprint, ideal for mobile-first sites',
    category: 'core',
    config: {
      templateId: 'compact',
      button: {
        shape: 'rounded-square',
        size: 'small',
        showOnlineIndicator: false,
        showLabel: false,
      },
      popup: {
        size: 'small',
        borderRadius: 8,
        animation: 'slide',
        shadowIntensity: 'light',
      },
      theme: {
        style: 'light',
        glassEffect: false,
        accentOnHeader: false,
      },
    },
  },
  {
    id: 'immersive',
    name: 'Immersive Voice',
    description: 'Full voice experience with rich animated UI',
    category: 'core',
    config: {
      templateId: 'immersive',
      button: {
        shape: 'circle',
        size: 'large',
        showOnlineIndicator: true,
        showLabel: false,
      },
      popup: {
        size: 'large',
        borderRadius: 16,
        animation: 'fade',
        shadowIntensity: 'heavy',
      },
      theme: {
        style: 'light',
        glassEffect: false,
        accentOnHeader: true,
      },
    },
  },

  // ===== BUTTON VARIANTS =====
  {
    id: 'pill-button',
    name: 'Pill Button',
    description: 'Horizontal button with "Chat with us" text',
    category: 'button-variants',
    config: {
      templateId: 'pill-button',
      button: {
        shape: 'pill',
        size: 'medium',
        showOnlineIndicator: true,
        showLabel: true,
        labelText: 'Chat with us',
      },
      popup: {
        size: 'medium',
        borderRadius: 12,
        animation: 'scale',
        shadowIntensity: 'medium',
      },
      theme: {
        style: 'light',
        glassEffect: false,
        accentOnHeader: true,
      },
    },
  },
  {
    id: 'side-tab',
    name: 'Side Tab',
    description: 'Vertical tab anchored to screen edge',
    category: 'button-variants',
    config: {
      templateId: 'side-tab',
      button: {
        shape: 'square',
        size: 'medium',
        showOnlineIndicator: false,
        showLabel: true,
        labelText: 'Help',
      },
      popup: {
        size: 'medium',
        borderRadius: 12,
        animation: 'slide',
        shadowIntensity: 'medium',
      },
      theme: {
        style: 'light',
        glassEffect: false,
        accentOnHeader: true,
      },
    },
  },
  {
    id: 'banner',
    name: 'Banner',
    description: 'Full-width bottom bar with avatar and CTA',
    category: 'button-variants',
    config: {
      templateId: 'banner',
      button: {
        shape: 'pill',
        size: 'small',
        showOnlineIndicator: true,
        showLabel: true,
        labelText: 'Need help? Talk to our assistant',
      },
      popup: {
        size: 'medium',
        borderRadius: 16,
        animation: 'slide',
        shadowIntensity: 'medium',
      },
      theme: {
        style: 'light',
        glassEffect: false,
        accentOnHeader: true,
      },
    },
  },

  // ===== VISUAL THEMES =====
  {
    id: 'glassmorphism',
    name: 'Glassmorphism',
    description: 'Frosted glass effect with modern translucent look',
    category: 'themes',
    config: {
      templateId: 'glassmorphism',
      button: {
        shape: 'circle',
        size: 'medium',
        showOnlineIndicator: true,
        showLabel: false,
      },
      popup: {
        size: 'medium',
        borderRadius: 20,
        animation: 'fade',
        shadowIntensity: 'light',
      },
      theme: {
        style: 'light',
        glassEffect: true,
        accentOnHeader: false,
      },
    },
  },
  {
    id: 'dark-mode',
    name: 'Dark Mode',
    description: 'Dark theme for sites with dark backgrounds',
    category: 'themes',
    config: {
      templateId: 'dark-mode',
      button: {
        shape: 'circle',
        size: 'medium',
        showOnlineIndicator: true,
        showLabel: false,
      },
      popup: {
        size: 'medium',
        borderRadius: 12,
        animation: 'scale',
        shadowIntensity: 'heavy',
      },
      theme: {
        style: 'dark',
        glassEffect: false,
        accentOnHeader: true,
      },
    },
  },
];

/**
 * Get a template by ID
 */
export const getWidgetTemplate = (id: string): WidgetTemplate | undefined => {
  return WIDGET_TEMPLATES.find((t) => t.id === id);
};

/**
 * Get templates by category
 */
export const getTemplatesByCategory = (category: 'core' | 'button-variants' | 'themes'): WidgetTemplate[] => {
  return WIDGET_TEMPLATES.filter((t) => t.category === category);
};

/**
 * Template category labels for display
 */
export const TEMPLATE_CATEGORY_LABELS: Record<'core' | 'button-variants' | 'themes', string> = {
  core: 'Core Styles',
  'button-variants': 'Button Variants',
  themes: 'Visual Themes',
};
